/**
 * JKL Announcement Bar
 * Custom element for announcement bar with Swiper slider
 * 
 * Migrated from: shopify-headless/app/components/global/AnnouncementBar.tsx
 * 
 * Features:
 * - Swiper slider for multiple announcements
 * - Autoplay with configurable rotation speed
 * - Pause on hover
 * - Close button with 24h localStorage persistence
 * - Pagination dots
 */

import Swiper from "./swiper-bundle.esm.browser.min.js";

class JklAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    
    // Swiper instance
    this.swiper = null;
    
    // Settings from data attributes
    this.sectionId = this.dataset.sectionId;
    this.rotationSpeed = parseInt(this.dataset.rotationSpeed, 10) || 5;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    
    // DOM elements
    this.swiperContainer = null;
    this.closeButton = null;
    
    // Bind methods
    this.initSwiper = this.initSwiper.bind(this);
    this.hideAnnouncement = this.hideAnnouncement.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // Small delay to ensure CSS is loaded
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    // Find Swiper container
    this.swiperContainer = this.querySelector('.jkl-announcement-swiper');
    
    // Find close button
    this.closeButton = this.querySelector('[data-close-announcement]');
    
    // Initialize Swiper only if container exists and has more than 1 slide
    if (this.swiperContainer) {
      const slidesCount = this.swiperContainer.querySelectorAll('.swiper-slide').length;
      if (slidesCount > 1) {
        this.initSwiper();
      }
    }
    
    // Add event listener for close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.hideAnnouncement);
    }
    
    // Listen for Shopify theme editor events
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
  }

  /**
   * Initialize Swiper slider
   */
  initSwiper() {
    // Destroy previous instance if exists
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }

    // Create new Swiper instance
    this.swiper = new Swiper(this.swiperContainer, {
      // Basic settings
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      speed: 500,
      allowTouchMove: true,
      
      // Important: set initial slide
      initialSlide: 0,
      
      // Autoplay
      autoplay: {
        delay: this.rotationSpeed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: this.pauseOnHover,
      },
      
      // Pagination
      pagination: {
        el: this.swiperContainer.querySelector('.swiper-pagination'),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
        renderBullet: (index, className) => {
          return `<span class="${className}"></span>`;
        },
      },
      
      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous announcement',
        nextSlideMessage: 'Next announcement',
      },
      
      // Observer to handle dynamic content
      observer: true,
      observeParents: true,
    });

    // Debug in theme editor
    if (window.Shopify && window.Shopify.designMode) {
      console.log('[JKL Announcement Bar] Swiper initialized', {
        rotationSpeed: this.rotationSpeed,
        pauseOnHover: this.pauseOnHover,
        slidesCount: this.swiper.slides.length,
      });
    }
  }

  /**
   * Hide announcement bar for 24 hours
   */
  hideAnnouncement() {
    // Save timestamp for 24 hours ahead
    const oneDayLater = new Date().getTime() + (24 * 60 * 60 * 1000);
    localStorage.setItem(`jkl-announcement-hidden-${this.sectionId}`, oneDayLater.toString());

    // Hide the element and collapse the spacer
    this.classList.remove('is-visible');
    document.documentElement.style.setProperty('--announcement-height', '0px');

    // Stop autoplay
    if (this.swiper) {
      this.swiper.autoplay.stop();
    }
  }

  /**
   * Handler for Shopify theme editor section reload
   */
  handleSectionLoad(event) {
    // Check if it's our section
    if (event.detail && event.detail.sectionId === this.sectionId) {
      // Reinitialize Swiper
      setTimeout(() => {
        this.init();
        this.classList.add('is-visible');
      }, 100);
    }
  }

  disconnectedCallback() {
    // Destroy Swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // Remove event listeners
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.hideAnnouncement);
    }
    
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
  }
}

// Register custom element
if (!customElements.get('jkl-announcement-bar')) {
  customElements.define('jkl-announcement-bar', JklAnnouncementBar);
}
